import { useMutationObserver } from "@vueuse/core";
import { computed, onMounted, ref, useAttrs } from "vue";
import { useExpose } from "#ce/composables/useExpose";
import { useHostElement } from "#ce/composables/useHostElement";
import { useHostEmit } from "#ce/composables/useHostEmit";

/**
 * Owns a single-choice value on the host. The `<option>` children of the host are the choices, the
 * host's live `value` property is the current choice, and `apply` dispatches a bubbling `input`
 * event and then a bubbling `change` event, each with the new value at `detail[0]`.
 *
 * A value that matches no option reads as the first option's value. The `value` attribute is the
 * default: the host adopts it, and a later change of it, until a user pick or a write to the `value`
 * property; a form reset adopts it again. Each option is `{ label, value, title, data }`, `data` being the option's `dataset`.
 */
export function useOptionsValue({ exposeValue = true } = {}) {
  const host = useHostElement();
  const attrs = useAttrs();
  const { emitBubbling } = useHostEmit();
  const { exposeProperties } = useExpose();

  const options = ref([]);
  const requestedValue = ref(attrs.value ?? "");
  let isValueDirty = false;

  const selectedValue = computed(() => {
    const requested = options.value.find((option) => option.value === requestedValue.value);

    return (requested ?? options.value[0])?.value ?? "";
  });
  const isBound = computed(() => options.value.length > 0);

  function adoptDefaultValue() {
    if (!isValueDirty) {
      requestedValue.value = host.getAttribute("value") ?? "";
    }
  }

  function sync() {
    adoptDefaultValue();
    options.value = Array.from(host.querySelectorAll(":scope > option")).map((option) => ({
      label: option.textContent?.trim() || "",
      value: option.value,
      title: option.title,
      data: { ...option.dataset },
    }));
  }

  function setValue(value) {
    isValueDirty = true;
    requestedValue.value = String(value ?? "");
  }

  function reset() {
    isValueDirty = false;
    requestedValue.value = host.getAttribute("value") ?? "";
  }

  function apply(value) {
    if (value == null || String(value) === selectedValue.value) {
      return false;
    }

    isValueDirty = true;
    requestedValue.value = String(value);
    emitBubbling("input", requestedValue.value);
    emitBubbling("change", requestedValue.value);

    return true;
  }

  if (exposeValue) {
    exposeProperties({
      value: {
        get: () => selectedValue.value,
        set: setValue,
      },
    });
  }

  useMutationObserver(host, sync, { attributes: true, childList: true, subtree: true, characterData: true });

  onMounted(sync);

  return { options, selectedValue, isBound, apply, sync, reset, setValue };
}
